import type { ReactNode } from 'react'
import { cn } from './cn'
import { Icon, type IconName } from './Icon'
import { Spinner } from '../shell/Spinner'

/**
 * Loading, empty and error states — ports of `Components/Shared/States/*.razor`.
 *
 * One place each, so "nothing here" and "this failed" look the same on every page. The old portal
 * had these components and pages still hand-rolled around them; the value is only realised if they
 * are actually used, so they are deliberately small and unopinionated.
 *
 * **A defect inherited from the CSS, not reproduced:** `.table-loading` and `.table-empty` pad with
 * `var(--spacing-xxl)`, which is one of the seventeen custom properties the Blazor themes never
 * declare — so that padding resolves to nothing today and those states sit flush against the table
 * edge. Here they get real padding from the token scale.
 */

export type StateVariant = 'page' | 'table' | 'inline'

/** `.page-loading` carries `min-height: 400px`; the table and inline variants do not. */
// Compact (Plans/experience-review-2026-10-10.md, Step 3): a 400px void for "nothing here" made empty
// pages read as broken ones. The message is in the body colour; only its explanation is muted.
const CONTAINER: Record<StateVariant, string> = {
  page: 'flex min-h-[240px] flex-col items-center justify-center gap-sm text-fg-secondary',
  table: 'flex flex-col items-center justify-center gap-sm px-md py-lg text-fg-secondary',
  inline: 'flex items-center gap-sm text-fg-secondary',
}

export interface LoadingStateProps {
  message?: string
  variant?: StateVariant
  className?: string
}

export function LoadingState({
  message = 'Loading…',
  variant = 'page',
  className,
}: LoadingStateProps) {
  return (
    <div className={cn(CONTAINER[variant], className)}>
      <Spinner />
      {message ? <p className="text-sm">{message}</p> : null}
    </div>
  )
}

export interface EmptyStateProps {
  message?: string
  /** The second line — why it is empty, or what to do about it. */
  subMessage?: string
  icon?: IconName
  variant?: StateVariant
  action?: { label: string; onClick: () => void }
  className?: string
}

export function EmptyState({
  message = 'No items found',
  subMessage,
  icon,
  variant = 'table',
  action,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn(CONTAINER[variant], className)}>
      {icon ? <Icon name={icon} size="lg" className="text-fg-muted" /> : null}
      <p className="text-sm font-medium text-fg">{message}</p>
      {subMessage ? <p className="text-xs text-fg-muted">{subMessage}</p> : null}
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          className="rounded-md bg-btn-primary-bg px-md py-sm text-sm text-btn-primary-fg hover:bg-btn-primary-hover"
        >
          {action.label}
        </button>
      ) : null}
    </div>
  )
}

export interface ErrorStateProps {
  message?: string
  /** The detail beneath — usually what the server actually said. */
  details?: string
  variant?: StateVariant
  onRetry?: () => void
  retryLabel?: string
  className?: string
}

export function ErrorState({
  message = 'An error occurred',
  details,
  variant = 'page',
  onRetry,
  retryLabel = 'Retry',
  className,
}: ErrorStateProps) {
  return (
    // `role="alert"` so a failure is announced rather than merely drawn. The Blazor original was a
    // plain div, so a screen-reader user learned nothing had loaded only by going looking.
    <div role="alert" className={cn(CONTAINER[variant], className)}>
      <Icon name="info" size="lg" className="text-danger" />
      <p className={cn('text-sm', variant === 'inline' && 'text-danger')}>{message}</p>
      {details ? <p className="text-xs text-fg-muted">{details}</p> : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border border-btn-secondary-border bg-btn-secondary-bg px-md py-sm text-sm text-btn-secondary-fg hover:bg-btn-secondary-hover"
        >
          {retryLabel}
        </button>
      ) : null}
    </div>
  )
}

export interface PageGateProps {
  /** False while the page is still working out whether it may render at all. */
  ready: boolean
  /** False when the signed-in user lacks the permission this page needs. */
  allowed?: boolean
  loadingMessage?: string
  deniedMessage?: string
  children: ReactNode
}

/**
 * The permission gate a page wraps itself in — a port of `PageLoadingGate.razor`.
 *
 * The original also handled "not authenticated" by rendering "Redirecting to login…" forever,
 * because the redirect itself lived elsewhere and sometimes never happened. Authentication is
 * `AuthGate`'s job here and is deliberately not duplicated: two components racing to decide whether
 * a page may render is how a page ends up permanently claiming to be redirecting.
 */
export function PageGate({
  ready,
  allowed = true,
  loadingMessage = 'Loading…',
  deniedMessage = 'You do not have permission to view this page.',
  children,
}: PageGateProps) {
  if (!ready) return <LoadingState message={loadingMessage} />

  // No Retry offered: the answer is identical until somebody changes the role. Same reasoning as
  // `ConnectError`, where `onRetry` became optional for exactly this case.
  if (!allowed) {
    return <ErrorState message={deniedMessage} />
  }

  return <>{children}</>
}
